import {QueryClient, useMutation, useQuery} from '@tanstack/react-query'
import {AxiosError} from 'axios'
import toast from 'react-hot-toast'

import clientSDK from '../../../App/ClientSDK'
import {ApiResponse} from '../../../App/types/apiResponse'

const baseUrl = 'hr/person-contracts'
const personsUrl = 'hr/persons'

type ErrorDetail = {
    message_template: string;
    message_parameters: { [ key: string ]: string };
    message: string;
};

type Errors = {
    general: ErrorDetail[];
};

type ApiError = {
    errors: Errors;
};

export type ApiPersonContract = {
    uuid: string,
    establishment_uuid: string,
    person_uuid: string,
    contract_week_minutes: number,
    start_date: string,
    end_date: string | null
}

export type ApiPersonContractCreate = {
    contract_week_minutes: number,
    start_date: string,
    end_date: string | null,
    person_uuid: string
}


type CreateApiResponse = ApiResponse<ApiPersonContract, {person_uuid: string}>

const createPersonContract = async (contractData: ApiPersonContractCreate) => {
    const response = await clientSDK
      .instance()
      .post<CreateApiResponse>(baseUrl, contractData)
    return response.data
    
}

export const useCreatePersonContractMutation = (queryClient: QueryClient) => {
    return useMutation({
        mutationFn: createPersonContract,
        onSettled: () => {
          queryClient.invalidateQueries({ queryKey: ['person-contracts'] })
        },
        onError: (error) => {
            const typedError = error as AxiosError<ApiError>

            if(typedError.response?.data.errors.general[0].message){
                toast.error(typedError.response?.data.errors.general[0].message || 'error')
            } else {
                toast.error('unknown error')
            }
        }
    })
}

type ApiUpdatePersonContract = {
    contract_week_minutes: number,
    start_date: string,
    end_date: string | null
}

const updatePersonContract = async (uuid: string, contractData: ApiUpdatePersonContract) => {
    const response = await clientSDK
      .instance()
      .put(`${baseUrl}/${uuid}`, contractData)
    return response.data
}

export const useUpdatePersonContractMutation = (queryClient: QueryClient) => {
    return useMutation({
        mutationFn: ({ uuid, contractData }: { uuid: string, contractData: ApiUpdatePersonContract }) => updatePersonContract(uuid, contractData),
        onSettled: () => {
          queryClient.invalidateQueries({ queryKey: ['person-contracts'] })
        },
    })
}

type PersonContractQueryFilters = {
    person_uuid: string
}
type ApiPersonContractsResponse = ApiResponse<ApiPersonContract[], PersonContractQueryFilters>

const getPersonContracts = async (personUuid: string): Promise<ApiPersonContract[]> => {
    const response = await clientSDK
      .instance()
      .get<ApiPersonContractsResponse>(`${personsUrl}/${personUuid}/contracts`)
    return response.data._collection
}

export const useGetPersonContractsQuery = (uuid: string) => {
    return useQuery({
        queryKey: ['person-contracts', uuid],
        queryFn: () => getPersonContracts(uuid),
    })
}

type ApiPersonContractResponse = {_resource: ApiPersonContract}

const getPersonContract = async (contractUuid: string): Promise<ApiPersonContract> => {
    const response = await clientSDK
      .instance()
      .get<ApiPersonContractResponse>(`${baseUrl}/${contractUuid}`)
    return response.data._resource
}

export const useGetPersonContractQuery = (uuid: string) => {
    return useQuery({
        queryKey: ['person-contract', uuid],
        queryFn: () => getPersonContract(uuid),
    })
}

const deletePersonContract = async (contractUuid: string): Promise<void> => {
    await clientSDK
      .instance()
      .delete(`${baseUrl}/${contractUuid}`)
}

export const useDeletePersonContractMutation = (queryClient: QueryClient) => {
    return useMutation({
        mutationFn: (uuid: string) => deletePersonContract(uuid),
        onSettled: () => {
          queryClient.invalidateQueries({ queryKey: ['person-contracts'] })
        },
    })
}
